import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  Settings,
  History,
  FileText,
  Loader2,
  RefreshCw,
  Phone,
  AlertTriangle,
  Edit,
  Save,
  Eye,
  ArrowLeft,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { smsApi, getErrorMessage } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort, formatDateTime } from "@/utils/dateFormat";

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    delivered: {
      color:
        "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
      icon: CheckCircle,
      label: "Delivered",
    },
    sent: {
      color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      icon: Send,
      label: "Sent",
    },
    queued: {
      color:
        "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
      icon: Clock,
      label: "Queued",
    },
    pending: {
      color: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400",
      icon: Clock,
      label: "Pending",
    },
    failed: {
      color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
      icon: XCircle,
      label: "Failed",
    },
    undelivered: {
      color:
        "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
      icon: AlertTriangle,
      label: "Undelivered",
    },
  };

  const {
    color,
    icon: Icon,
    label,
  } = config[status] || {
    color: "bg-gray-100 text-gray-700",
    icon: Clock,
    label: status,
  };

  return (
    <Badge className={`${color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  );
};

// Message type badge
const TypeBadge = ({ type }) => {
  const config = {
    verification: {
      color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30",
      label: "Verification",
    },
    reminder: {
      color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30",
      label: "Reminder",
    },
    completion: {
      color: "bg-green-100 text-green-700 dark:bg-green-900/30",
      label: "Completion",
    },
    expiration: {
      color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30",
      label: "Expiration",
    },
    signing_link: {
      color: "bg-primary/10 text-primary dark:bg-primary/20",
      label: "Signing Link",
    },
    in_person: {
      color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30",
      label: "In-Person",
    },
    custom: {
      color: "bg-gray-100 text-gray-700 dark:bg-gray-900/30",
      label: "Custom",
    },
  };

  const { color, label } = config[type] || {
    color: "bg-gray-100 text-gray-700",
    label: type,
  };

  return (
    <Badge variant="outline" className={color}>
      {label}
    </Badge>
  );
};

// Stat card component
const StatCard = ({
  title,
  value,
  description,
  icon: Icon,
  color = "primary",
}) => {
  const colorClasses = {
    primary: "bg-primary/10 text-primary",
    green:
      "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    red: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
    yellow:
      "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400",
    blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
};

export const SMSSettings = () => {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("settings");

  // Local settings state (for form edits)
  const [localSettings, setLocalSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  // Dialog states
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templatePreview, setTemplatePreview] = useState("");

  // Filter states
  const [filters, setFilters] = useState({ status: "", messageType: "" });

  // ============================================
  // TanStack Query - Fetch All SMS Data
  // ============================================
  const {
    data: smsData,
    isLoading: loading,
    refetch: loadData,
  } = useQuery({
    queryKey: ["sms-settings-data"],
    queryFn: async () => {
      const [statusRes, settingsRes, statsRes, templatesRes] =
        await Promise.all([
          smsApi
            .getStatus()
            .catch(() => ({ data: { data: { enabled: false } } })),
          smsApi.getSettings().catch(() => ({ data: { data: null } })),
          smsApi.getStats().catch(() => ({ data: { data: null } })),
          smsApi.getTemplates().catch(() => ({ data: { data: [] } })),
        ]);

      return {
        status: statusRes.data.data,
        settings: settingsRes.data.data,
        stats: statsRes.data.data,
        templates: templatesRes.data.data || [],
      };
    },
    meta: {
      onError: (error) => {
        toast.error(getErrorMessage(error, t("sms.errors.loadFailed")));
      },
    },
  });

  const status = smsData?.status;
  const settings = localSettings || smsData?.settings;
  const stats = smsData?.stats;
  const templates = smsData?.templates || [];

  // Sync local settings when query data changes
  useEffect(() => {
    if (smsData?.settings && !localSettings) {
      setLocalSettings(smsData?.settings);
    }
  }, [localSettings, smsData?.settings]);

  // ============================================
  // TanStack Query - Fetch Messages
  // ============================================
  const { refetch: loadMessages } = useQuery({
    queryKey: ["sms-messages", pagination.page, filters],
    queryFn: async () => {
      const response = await smsApi.getMessages({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      const { data } = response.data;
      setMessages(data.messages || data.items || []);
      setPagination((prev) => ({
        ...prev,
        total: data.pagination?.total || data.total || 0,
        pages: data.pagination?.pages || data.pages || 0,
      }));
      return data;
    },
    enabled: activeTab === "history",
  });

  useEffect(() => {
    if (activeTab === "history") {
      loadMessages();
    }
  }, [activeTab, pagination.page, filters, loadMessages]);

  // ============================================
  // TanStack Query - Mutations
  // ============================================
  const saveSettingsMutation = useMutation({
    mutationFn: (settingsData) => smsApi.updateSettings(settingsData),
    onSuccess: () => {
      toast.success(t("sms.settings.saved"));
      queryClient.invalidateQueries({ queryKey: ["sms-settings-data"] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t("sms.errors.saveFailed")));
    },
  });

  const saveTemplateMutation = useMutation({
    mutationFn: (template) => smsApi.upsertTemplate(template),
    onSuccess: () => {
      toast.success(t("sms.templates.saved"));
      setShowTemplateDialog(false);
      queryClient.invalidateQueries({ queryKey: ["sms-settings-data"] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t("sms.errors.templateSaveFailed")));
    },
  });

  const saving =
    saveSettingsMutation.isPending || saveTemplateMutation.isPending;

  const handleSaveSettings = () => {
    saveSettingsMutation.mutate(localSettings);
  };

  const handleViewMessage = async (message) => {
    try {
      const response = await smsApi.getMessage(message.id);
      setSelectedMessage(response.data.data);
      setShowMessageDialog(true);
    } catch (error) {
      toast.error(getErrorMessage(error, t("sms.errors.loadDetailsFailed")));
    }
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate({ ...template });
    setTemplatePreview("");
    setShowTemplateDialog(true);
  };

  const handlePreviewTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      const response = await smsApi.previewTemplate(
        selectedTemplate.template_key,
        {
          recipient_name: "John Doe",
          document_name: "Sample Agreement",
          sender_name: user?.name || "Your Company",
          signing_link: "https://app.escribe.io/sign/abc123",
          code: "123456",
          expiry_minutes: "10",
          hours_remaining: "24",
          app_name: "eScribe",
        },
      );
      setTemplatePreview(response.data.data.preview);
    } catch (error) {
      toast.error(getErrorMessage(error, t("sms.errors.previewFailed")));
    }
  };

  const handleSaveTemplate = () => {
    if (!selectedTemplate) return;
    saveTemplateMutation.mutate(selectedTemplate);
  };

  // Update local settings helper
  const setSettings = (updater) => {
    if (typeof updater === "function") {
      setLocalSettings((prev) => updater(prev));
    } else {
      setLocalSettings(updater);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/settings"
            className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border border-border hover:bg-accent transition-colors touch-target"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{t("sms.title")}</h1>
            <p className="text-muted-foreground">{t("sms.description")}</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={loadData}
          size={isMobile ? "mobile" : "default"}
          className={isMobile ? "w-full" : ""}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          {t("common.refresh")}
        </Button>
      </div>

      {/* Status Alert */}
      {!status?.enabled && (
        <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
          <div className="flex gap-3">
            <div className="shrink-0">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/10">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                {t("sms.notConfigured.title")}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("sms.notConfigured.description")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <StatCard
            title={t("sms.stats.messagesSent")}
            value={stats.totalSent?.toLocaleString() || "0"}
            description={t("sms.stats.last30Days")}
            icon={Send}
            color="blue"
          />
          <StatCard
            title={t("sms.stats.delivered")}
            value={stats.totalDelivered?.toLocaleString() || "0"}
            description={t("sms.stats.deliveryRate", {
              rate: stats.deliveryRate || 0,
            })}
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title={t("sms.stats.failed")}
            value={stats.totalFailed?.toLocaleString() || "0"}
            description={t("sms.stats.requiresAttention")}
            icon={XCircle}
            color="red"
          />
          <StatCard
            title={t("sms.stats.creditsUsed")}
            value={stats.creditsUsed?.toLocaleString() || "0"}
            description={t("sms.stats.smsSegments")}
            icon={MessageSquare}
            color="primary"
          />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            {t("sms.tabs.settings")}
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="h-4 w-4" />
            {t("sms.tabs.templates")}
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            {t("sms.tabs.history")}
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("sms.settings.title")}</CardTitle>
              <CardDescription>
                {t("sms.settings.enableNotificationsDesc")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* SMS Enabled */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-base">
                    {t("sms.settings.enableNotifications")}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t("sms.settings.enableNotificationsDesc")}
                  </p>
                </div>
                <Switch
                  checked={settings?.sms_enabled ?? true}
                  onCheckedChange={(checked) =>
                    setSettings((prev) => ({ ...prev, sms_enabled: checked }))
                  }
                />
              </div>

              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium">
                  {t("sms.settings.notificationTypes")}
                </h4>

                {/* Verification */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{t("sms.settings.verification")}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t("sms.settings.verificationDesc")}
                    </p>
                  </div>
                  <Switch
                    checked={settings?.verification_enabled ?? true}
                    onCheckedChange={(checked) =>
                      setSettings((prev) => ({
                        ...prev,
                        verification_enabled: checked,
                      }))
                    }
                  />
                </div>

                {/* Reminders */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{t("sms.settings.reminders")}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t("sms.settings.remindersDesc")}
                    </p>
                  </div>
                  <Switch
                    checked={settings?.reminders_enabled ?? true}
                    onCheckedChange={(checked) =>
                      setSettings((prev) => ({
                        ...prev,
                        reminders_enabled: checked,
                      }))
                    }
                  />
                </div>

                {/* Completion */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{t("sms.settings.completion")}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t("sms.settings.completionDesc")}
                    </p>
                  </div>
                  <Switch
                    checked={settings?.completion_notifications_enabled ?? true}
                    onCheckedChange={(checked) =>
                      setSettings((prev) => ({
                        ...prev,
                        completion_notifications_enabled: checked,
                      }))
                    }
                  />
                </div>

                {/* Expiration */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>{t("sms.settings.expiration")}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t("sms.settings.expirationDesc")}
                    </p>
                  </div>
                  <Switch
                    checked={settings?.expiration_warnings_enabled ?? true}
                    onCheckedChange={(checked) =>
                      setSettings((prev) => ({
                        ...prev,
                        expiration_warnings_enabled: checked,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium">{t("sms.settings.rateLimits")}</h4>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t("sms.settings.maxPerEnvelope")}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      value={settings?.max_sms_per_envelope || 10}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          max_sms_per_envelope: parseInt(e.target.value),
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("sms.settings.maxPerEnvelopeDesc")}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>{t("sms.settings.maxPerRecipientDay")}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={settings?.max_sms_per_recipient_per_day || 5}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          max_sms_per_recipient_per_day: parseInt(
                            e.target.value,
                          ),
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("sms.settings.maxPerRecipientDayDesc")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium">
                  {t("sms.settings.senderConfig")}
                </h4>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t("sms.settings.senderType")}</Label>
                    <Select
                      value={settings?.sender_type || "phone"}
                      onValueChange={(value) =>
                        setSettings((prev) => ({ ...prev, sender_type: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">
                          {t("sms.settings.phoneNumber")}
                        </SelectItem>
                        <SelectItem value="alphanumeric">
                          {t("sms.settings.alphanumericId")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t("sms.settings.alphanumericNote")}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>{t("sms.settings.senderId")}</Label>
                    <Input
                      placeholder={
                        settings?.sender_type === "alphanumeric"
                          ? "eScribe"
                          : "+61412345678"
                      }
                      value={settings?.default_sender_id || ""}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          default_sender_id: e.target.value,
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {t("sms.settings.senderIdDesc")}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button onClick={handleSaveSettings} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("sms.templates.title")}</CardTitle>
              <CardDescription>
                Customize the message templates sent to your signers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.template")}</TableHead>
                    <TableHead>{t("common.type")}</TableHead>
                    <TableHead>{t("common.usage")}</TableHead>
                    <TableHead className="text-right">
                      {t("common.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No templates available
                      </TableCell>
                    </TableRow>
                  ) : (
                    templates.map((template) => (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{template.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {template.description}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <TypeBadge type={template.message_type} />
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-foreground">
                            {template.usage_count?.toLocaleString() || 0} times
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditTemplate(template)}
                            disabled={template.is_system}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            {template.is_system ? "View" : "Edit"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t("sms.history.title")}</CardTitle>
                  <CardDescription>
                    {t("sms.history.description")}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Select
                    value={filters.status || "all"}
                    onValueChange={(value) => {
                      setFilters((prev) => ({
                        ...prev,
                        status: value === "all" ? "" : value,
                      }));
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                  >
                    <SelectTrigger className="w-35">
                      <SelectValue
                        placeholder={t("sms.history.filterStatus")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t("sms.history.filterStatus")}
                      </SelectItem>
                      <SelectItem value="delivered">
                        {t("sms.status.delivered")}
                      </SelectItem>
                      <SelectItem value="sent">
                        {t("sms.status.sent")}
                      </SelectItem>
                      <SelectItem value="failed">
                        {t("sms.status.failed")}
                      </SelectItem>
                      <SelectItem value="undelivered">
                        {t("sms.status.undelivered")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={filters.messageType || "all"}
                    onValueChange={(value) => {
                      setFilters((prev) => ({
                        ...prev,
                        messageType: value === "all" ? "" : value,
                      }));
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                  >
                    <SelectTrigger className="w-35">
                      <SelectValue placeholder={t("sms.history.filterType")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t("sms.history.filterType")}
                      </SelectItem>
                      <SelectItem value="verification">
                        {t("sms.templates.types.verification")}
                      </SelectItem>
                      <SelectItem value="reminder">
                        {t("sms.templates.types.reminder")}
                      </SelectItem>
                      <SelectItem value="completion">
                        {t("sms.templates.types.completion")}
                      </SelectItem>
                      <SelectItem value="expiration">
                        {t("sms.templates.types.expiration")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("sms.history.recipient")}</TableHead>
                    <TableHead>{t("sms.history.type")}</TableHead>
                    <TableHead>{t("sms.history.status")}</TableHead>
                    <TableHead>{t("sms.history.segments")}</TableHead>
                    <TableHead>{t("sms.history.date")}</TableHead>
                    <TableHead className="text-right">
                      {t("common.actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 text-muted-foreground"
                      >
                        {t("sms.history.noMessages")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    messages.map((message) => (
                      <TableRow key={message.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div className="font-medium">
                                {message.recipient_name || t("common.unknown")}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {message.recipient_phone_formatted ||
                                  message.recipient_phone}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <TypeBadge type={message.message_type} />
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={message.status} />
                        </TableCell>
                        <TableCell>{message.message_segments || 1}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDateShort(message.created_at, user)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewMessage(message)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
            {pagination.pages > 1 && (
              <CardFooter className="flex items-center justify-between border-t py-4">
                <p className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.pages} (
                  {pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page >= pagination.pages}
                  >
                    Next
                  </Button>
                </div>
              </CardFooter>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Message Detail Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("sms.history.viewDetails")}</DialogTitle>
          </DialogHeader>
          {selectedMessage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">
                    {t("sms.history.recipient")}
                  </Label>
                  <p className="font-medium">
                    {selectedMessage.recipient_name || t("common.unknown")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {selectedMessage.recipient_phone_formatted}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("sms.history.status")}
                  </Label>
                  <div className="mt-1">
                    <StatusBadge status={selectedMessage.status} />
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("sms.history.type")}
                  </Label>
                  <div className="mt-1">
                    <TypeBadge type={selectedMessage.message_type} />
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("sms.history.segmentsCredits")}
                  </Label>
                  <p className="font-medium">
                    {selectedMessage.message_segments} /{" "}
                    {selectedMessage.credits_used}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("sms.history.created")}
                  </Label>
                  <p className="text-sm">
                    {formatDateTime(selectedMessage.created_at)}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    {t("sms.history.delivered")}
                  </Label>
                  <p className="text-sm">
                    {selectedMessage.delivered_at
                      ? formatDateTime(selectedMessage.delivered_at)
                      : "-"}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-muted-foreground">
                  {t("sms.history.messageContent")}
                </Label>
                <div className="mt-1 p-3 bg-muted rounded-lg text-sm">
                  {selectedMessage.message_content}
                </div>
              </div>

              {selectedMessage.provider_error_message && (
                <div>
                  <Label className="text-red-600">{t("common.error")}</Label>
                  <div className="mt-1 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-700 dark:text-red-300">
                    <strong>{selectedMessage.provider_error_code}:</strong>{" "}
                    {selectedMessage.provider_error_message}
                  </div>
                </div>
              )}

              {selectedMessage.envelope_title && (
                <div>
                  <Label className="text-muted-foreground">
                    {t("sms.history.relatedEnvelope")}
                  </Label>
                  <p className="text-sm">{selectedMessage.envelope_title}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMessageDialog(false)}
            >
              {t("common.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Edit Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedTemplate?.is_system
                ? t("sms.templates.view")
                : t("sms.templates.edit")}{" "}
              {t("sms.templates.template")}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate?.is_system
                ? t("sms.templates.systemNote")
                : t("sms.templates.customNote")}
            </DialogDescription>
          </DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("common.name")}</Label>
                  <Input
                    value={selectedTemplate.name}
                    onChange={(e) =>
                      setSelectedTemplate((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    disabled={selectedTemplate.is_system}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("sms.history.type")}</Label>
                  <Input value={selectedTemplate.message_type} disabled />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("sms.history.messageContent")}</Label>
                <Textarea
                  value={selectedTemplate.content}
                  onChange={(e) =>
                    setSelectedTemplate((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }))
                  }
                  disabled={selectedTemplate.is_system}
                  rows={4}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  {t("sms.templates.availableVariables")}:{" "}
                  {selectedTemplate.variables
                    ?.map((v) => `{{${v.name}}}`)
                    .join(", ")}
                </p>
              </div>

              {templatePreview && (
                <div className="space-y-2">
                  <Label>{t("sms.templates.preview")}</Label>
                  <div className="p-3 bg-muted rounded-lg text-sm">
                    {templatePreview}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handlePreviewTemplate}>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowTemplateDialog(false)}
            >
              Cancel
            </Button>
            {!selectedTemplate?.is_system && (
              <Button onClick={handleSaveTemplate} disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMSSettings;
